import { useAuth } from '@/contexts/AuthContext';
import { cn, getProfilePictureUrl } from '@/lib/utils';
import { LogOut, Menu, Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';

interface TopbarProps {
    onMenuClick: () => void;
    title?: string;
    actions?: React.ReactNode;
    isSidebarCollapsed?: boolean;
}

export function Topbar({ onMenuClick, title, actions, isSidebarCollapsed }: TopbarProps) {
    const { user, signOut } = useAuth();
    const navigate = useNavigate();
    const { theme, setTheme } = useTheme();

    const handleSignOut = async () => {
        await signOut();
        navigate('/');
    };

    return (
        <header className={cn(
            "fixed top-0 right-0 z-30 h-16 bg-background/80 backdrop-blur-md border-b border-border",
            "flex items-center justify-between px-4 md:px-6",
            "transition-all duration-300 ease-out-expo",
            // Adjust left position based on sidebar state
            "left-0 lg:left-64",
            isSidebarCollapsed && "lg:left-20"
        )}>
            {/* Left section */}
            <div className="flex items-center gap-4">
                {/* Mobile menu button */}
                <Button
                    variant="ghost"
                    size="icon"
                    className="lg:hidden"
                    onClick={onMenuClick}
                >
                    <Menu className="w-5 h-5" />
                </Button>

                {/* Page title */}
                {title && (
                    <h1 className="text-lg font-semibold text-foreground hidden sm:block">
                        {title}
                    </h1>
                )}
            </div>

            {/* Right section */}
            <div className="flex items-center gap-2">
                {/* Custom actions slot */}
                {actions}

                {/* Theme toggle */}
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                    className="text-muted-foreground hover:text-foreground"
                >
                    <Sun className="h-5 w-5 rotate-0 scale-100 transition-transform dark:-rotate-90 dark:scale-0" />
                    <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-transform dark:rotate-0 dark:scale-100" />
                    <span className="sr-only">Toggle theme</span>
                </Button>

                {/* User menu */}
                {user && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                className="relative h-9 w-9 rounded-full hover:ring-2 hover:ring-primary/20"
                            >
                                <Avatar className="h-9 w-9">
                                    <AvatarImage src={getProfilePictureUrl(user.email)} alt={user.name} />
                                    <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                                        {user.name?.charAt(0) || user.email?.charAt(0)}
                                    </AvatarFallback>
                                </Avatar>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            <div className="flex items-center gap-3 p-3">
                                <Avatar className="h-10 w-10">
                                    <AvatarImage src={getProfilePictureUrl(user.email)} alt={user.name} />
                                    <AvatarFallback className="bg-primary/10 text-primary">
                                        {user.name?.charAt(0) || user.email?.charAt(0)}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-foreground truncate">
                                        {user.name || user.email}
                                    </p>
                                    <p className="text-xs text-muted-foreground capitalize">
                                        {user.role}
                                    </p>
                                </div>
                            </div>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onClick={handleSignOut}
                                className="text-destructive focus:text-destructive cursor-pointer"
                            >
                                <LogOut className="mr-2 h-4 w-4" />
                                <span>Sign out</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>
        </header>
    );
}

export default Topbar;

